/**
 * XP Store - Gamification system for Mellow
 * Tracks daily XP, total XP, and streaks
 * Simple direct Supabase calls (like chats$) - no complex sync
 */

import { observable } from '@legendapp/state';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { trackEvent, incrementUserProperties, updateUserProperties } from '../lib/analytics';
import { logger } from '../lib/logger';

// XP state
export const xp$ = observable({
  todayXP: 0,
  totalXP: 0,
  streak: 0,
  last7Days: [],  // Array of { date, xp }
  loading: false,
});

/**
 * Get today's date in local timezone as YYYY-MM-DD
 */
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Fetch XP data from server
 */
export const fetchXPData = async () => {
  const user = auth$.user.get();
  if (!user) {
    xp$.loading.set(false);
    return;
  }

  xp$.loading.set(true);

  try {
    // Fetch last 30 days of XP activity
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const startDate = thirtyDaysAgo.toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('mellow_xp_activity')
      .select('date, xp_earned')
      .eq('user_id', user.id)
      .gte('date', startDate)
      .order('date', { ascending: false });

    if (error) throw error;

    const activities = data || [];
    const todayDate = getTodayDate();

    // Aggregate XP by date (sum across all languages)
    const xpByDate = {};
    activities.forEach(activity => {
      const date = activity.date;
      xpByDate[date] = (xpByDate[date] || 0) + (activity.xp_earned || 0);
    });

    // Calculate today's XP (sum all language records for today)
    const todayXP = xpByDate[todayDate] || 0;

    // Calculate total XP (sum of all time)
    const { data: totalData, error: totalError } = await supabase
      .from('mellow_xp_activity')
      .select('xp_earned')
      .eq('user_id', user.id);

    if (totalError) throw totalError;

    const totalXP = (totalData || []).reduce((sum, a) => sum + (a.xp_earned || 0), 0);

    // Calculate streak (consecutive days with XP > 0)
    // Use aggregated xpByDate instead of raw activities
    const previousStreak = xp$.streak.get();
    let streak = 0;
    for (let i = 0; i < 365; i++) { // Check up to 365 days
      const expectedDate = new Date();
      expectedDate.setDate(expectedDate.getDate() - i);
      const expectedDateStr = expectedDate.toISOString().split('T')[0];

      const dayXP = xpByDate[expectedDateStr] || 0;
      if (dayXP > 0) {
        streak++;
      } else {
        break;
      }
    }

    // Track streak milestones (only when streak increases)
    if (streak > previousStreak && [3, 7, 14, 30, 60, 100].includes(streak)) {
      trackEvent('streak_milestone', {
        streak_days: streak,
      });
    }

    // Update current_streak user property if changed
    if (streak !== previousStreak) {
      updateUserProperties({ current_streak: streak });
    }

    // Get last 7 days (sum XP across all languages per day)
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      last7Days.push({
        date: dateStr,
        xp: xpByDate[dateStr] || 0,
      });
    }

    xp$.assign({
      todayXP,
      totalXP,
      streak,
      last7Days,
      loading: false,
    });
  } catch (err) {
    logger.error('Failed to fetch XP data', { error: err.message });
    xp$.loading.set(false);
  }
};

/**
 * Add XP (optimistic update + server sync)
 * @param {number} amount - XP amount to add (can be negative)
 * @param {string} actionType - Type of action (review, practice, save)
 * @param {string} [languageCode] - Optional language code for language-specific XP
 */
export const addXP = async (amount, actionType, languageCode = null) => {
  const user = auth$.user.get();
  if (!user) return;

  // Optimistic update - prevent going below 0
  const currentXP = xp$.todayXP.get();
  const newXP = Math.max(0, currentXP + amount);
  xp$.todayXP.set(newXP);
  xp$.totalXP.set(v => Math.max(0, v + amount));

  // Track XP earned event (Plan B - Behavioral Tracking)
  trackEvent('xp_earned', {
    amount,
    source: actionType, // 'review', 'discovery', 'chat'
    language_code: languageCode,
  });

  // Increment total_xp user property
  incrementUserProperties({ total_xp: amount });

  // Sync to server
  try {
    const todayDate = getTodayDate();
    const action = {
      type: actionType,
      timestamp: new Date().toISOString(),
      amount,
    };

    // Build query for existing record
    let query = supabase
      .from('mellow_xp_activity')
      .select('id, xp_earned, actions')
      .eq('user_id', user.id)
      .eq('date', todayDate);

    // Add language_code filter (NULL or specific code)
    if (languageCode) {
      query = query.eq('language_code', languageCode);
    } else {
      query = query.is('language_code', null);
    }

    const { data: existing } = await query.maybeSingle();

    if (existing) {
      // Update existing record
      const updatedXP = Math.max(0, (existing.xp_earned || 0) + amount);
      const updatedActions = [...(existing.actions || []), action];

      await supabase
        .from('mellow_xp_activity')
        .update({
          xp_earned: updatedXP,
          actions: updatedActions,
        })
        .eq('id', existing.id);
    } else {
      // Insert new record
      await supabase
        .from('mellow_xp_activity')
        .insert({
          user_id: user.id,
          date: todayDate,
          xp_earned: Math.max(0, amount),
          actions: [action],
          language_code: languageCode,
        });
    }

    // Refresh data to get accurate streak
    await fetchXPData();
  } catch (err) {
    logger.error('Failed to sync XP', { error: err.message, amount, actionType });
    // Revert optimistic update on error
    xp$.todayXP.set(currentXP);
  }
};

/**
 * Get today's XP count
 */
export const getTodayXP = () => xp$.todayXP.get();

/**
 * Get total XP count
 */
export const getTotalXP = () => xp$.totalXP.get();

/**
 * Get current streak
 */
export const getStreak = () => xp$.streak.get();

/**
 * Get total XP for a specific language
 * @param {string} languageCode - Language code (e.g., 'en', 'ja')
 * @returns {Promise<number>} Total XP for the language
 */
export const getLanguageXP = async (languageCode) => {
  const user = auth$.user.get();
  if (!user || !languageCode) return 0;

  try {
    const { data, error } = await supabase
      .from('mellow_xp_activity')
      .select('xp_earned')
      .eq('user_id', user.id)
      .eq('language_code', languageCode);

    if (error) throw error;

    return (data || []).reduce((sum, a) => sum + (a.xp_earned || 0), 0);
  } catch (err) {
    console.error('Failed to fetch language XP:', err);
    return 0;
  }
};

